import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { BehaviorSubject } from 'rxjs';
import { AccessTokenRequest } from 'edu-auth/dist/request/access-token.request';
import {
  UntypedFormControl,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoginService } from '../login.service';

const MAGIC_LINK_EMAIL_ERROR = 'magicLinkEmail';
const MAGIC_LINK_EMAIL_NOT_FOUND = 'emailNotFound';
const MAGIC_LINK_CODE_PATTERN = /^\d{6}$/;
const MSG_LOGIN_LINK_SENT = 'A login link was sent to your email.';
const MSG_EMAIL_NOT_FOUND = 'No account found for this email address.';
const DEFAULT_MAGIC_LINK_REQUEST_FAILED = 'Unable to send login link.';
const DEFAULT_MAGIC_LINK_CODE_INVALID = 'Code is invalid or expired.';
const SNACK_DURATION_MS = 3500;
const SNACK_DURATION_LONG_MS = 5500;
const MAGIC_LINK_COOLDOWN_MESSAGE =
  /please wait (\d+) seconds? before requesting another login link/i;

@Component({
  selector: 'app-form-login',
  templateUrl: './form-login.component.html',
  styleUrls: ['./form-login.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('screenAnim', [
      transition('void => forward', [
        style({ opacity: 0, transform: 'translateX(28px)' }),
        animate(
          '260ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateX(0)' }),
        ),
      ]),
      transition('forward => void', [
        style({ position: 'absolute', top: '0', left: '0', right: '0' }),
        animate(
          '180ms ease-in',
          style({ opacity: 0, transform: 'translateX(-20px)' }),
        ),
      ]),
      transition('void => back', [
        style({ opacity: 0, transform: 'translateX(-28px)' }),
        animate(
          '240ms 200ms ease-out',
          style({ opacity: 1, transform: 'translateX(0)' }),
        ),
      ]),
      transition('back => void', [
        style({
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          zIndex: 10,
        }),
        animate('200ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
    trigger('passwordField', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-6px)' }),
        animate(
          '200ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
    trigger('codeEntry', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-8px)' }),
        animate(
          '220ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
  ],
})
export class FormLoginComponent implements OnDestroy {
  @Input() loginStream: BehaviorSubject<AccessTokenRequest>;
  @Input() redirectTo?: string;
  @Input() isLoading = false;
  @Output() magicLinkSessionEstablished = new EventEmitter<void>();

  /** API error text for magic-link request (shown under the username field). */
  magicLinkRequestError: string | null = null;

  /** Shown under the email field when the address is not registered. */
  emailNotFoundMessage: string | null = null;

  userNameController = new UntypedFormControl('', [Validators.required]);
  passwordController = new UntypedFormControl('');
  readonly magicLinkCodeBoxIndexes = [0, 1, 2, 3, 4, 5];
  readonly magicLinkCodeDigitControls = Array.from(
    { length: 6 },
    () => new UntypedFormControl(''),
  );

  @ViewChildren('magicLinkCodeBox')
  private magicLinkCodeBoxRefs!: QueryList<ElementRef<HTMLInputElement>>;
  screenDir: 'forward' | 'back' | 'idle' = 'idle';
  hidePassword: boolean = true;
  magicLinkRequested: boolean = false;
  requestingMagicLink: boolean = false;
  verifyingMagicLinkCode: boolean = false;
  passwordStep: boolean = false;

  /** Email used for the last successful magic-link request (for code verification). */
  magicLinkRecipientEmail = '';
  magicLinkCodeSecondsRemaining = 0;
  /** Total seconds for the active countdown ring (default or server cooldown). */
  magicLinkCodeCountdownTotalSeconds = 45;
  showMagicLinkCodeField = false;

  /** Shown under OTP inputs when the server rejects the code. */
  magicLinkCodeVerifyError: string | null = null;

  private magicLinkCodeTimerId: ReturnType<typeof setInterval> | null = null;

  /** Countdown length before the verification code field appears (must match timer logic). */
  readonly magicLinkCodeWaitSeconds = 45;

  constructor(
    private loginService: LoginService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnDestroy(): void {
    this.clearMagicLinkCodeTimer();
  }

  get hasIdentifier(): boolean {
    const v = this.userNameController.value as string | null | undefined;
    return !!(v && String(v).trim());
  }

  get magicLinkCodeComplete(): boolean {
    return this.magicLinkCodeDigitControls.every((c) =>
      /^[0-9]$/.test(String(c.value ?? '')),
    );
  }

  get magicLinkCodeBoxesTouched(): boolean {
    return this.magicLinkCodeDigitControls.some((c) => c.touched);
  }

  get magicLinkResendBlocked(): boolean {
    return this.magicLinkRequested && this.magicLinkCodeSecondsRemaining > 0;
  }

  private getJoinedMagicLinkCode(): string {
    return this.magicLinkCodeDigitControls
      .map((c) => String(c.value ?? ''))
      .join('');
  }

  private focusMagicLinkCodeBox(index: number): void {
    const focus = () => {
      const boxes = this.magicLinkCodeBoxRefs?.toArray();
      const el = boxes[index]?.nativeElement;
      if (el) {
        el.focus();
        el.select();
      }
    };
    setTimeout(focus);
  }

  private resetMagicLinkCodeBoxes(): void {
    this.clearMagicLinkVerifyError();
    for (const c of this.magicLinkCodeDigitControls) {
      c.setValue('');
      c.markAsUntouched();
    }
  }

  private clearMagicLinkVerifyError(): void {
    this.magicLinkCodeVerifyError = null;
  }

  handleKeyUp(e: any) {
    if (e.keyCode === 13) {
      this.login();
    }
  }

  continueToPassword(): void {
    this.clearMagicLinkEmailError();
    this.magicLinkRequestError = null;
    const raw = (this.userNameController.value as string).trim();
    this.userNameController.setValue(raw);
    if (!raw) {
      this.userNameController.markAsTouched();
      return;
    }
    this.passwordStep = true;
    this.passwordController.setValidators([Validators.required]);
    this.passwordController.updateValueAndValidity();
  }

  async login() {
    if (!this.passwordStep) {
      return;
    }
    this.clearMagicLinkEmailError();
    this.magicLinkRequestError = null;
    if (this.passwordController.invalid || this.userNameController.invalid) {
      return;
    }
    let request = {
      username: (this.userNameController.value as string).trim(),
      password: this.passwordController.value.trim(),
      grant_type: 'password',
      scope: 'angular',
    };
    this.loginStream.next(request);
  }

  togglePasswordVisibility() {
    this.hidePassword = !this.hidePassword;
  }

  private exitPasswordStep(): void {
    this.passwordStep = false;
    this.passwordController.clearValidators();
    this.passwordController.setValue('');
    this.passwordController.markAsUntouched();
    this.passwordController.updateValueAndValidity();
  }

  switchToMagicLinkFromPassword(): void {
    this.exitPasswordStep();
    void this.requestMagicLink();
  }

  backFromMagicLink(): void {
    this.screenDir = 'back';
    this.clearMagicLinkCodeTimer();
    this.magicLinkRequested = false;
    this.showMagicLinkCodeField = false;
    this.magicLinkCodeSecondsRemaining = 0;
    this.resetMagicLinkCodeBoxes();
    this.magicLinkCodeVerifyError = null;
    if (this.magicLinkRecipientEmail) {
      this.userNameController.setValue(this.magicLinkRecipientEmail);
    }
    this.passwordStep = false;
  }

  async requestMagicLink() {
    this.clearMagicLinkEmailError();
    this.magicLinkRequestError = null;
    this.emailNotFoundMessage = null;
    const raw = (this.userNameController.value as string).trim();
    this.userNameController.setValue(raw);
    if (!raw) {
      this.userNameController.markAsTouched();
      return;
    }
    const emailProbe = new UntypedFormControl(raw, Validators.email);
    if (emailProbe.invalid) {
      this.userNameController.setErrors({
        ...(this.userNameController.errors ?? {}),
        [MAGIC_LINK_EMAIL_ERROR]: true,
      });
      this.userNameController.markAsTouched();
      return;
    }

    if (this.magicLinkResendBlocked) {
      return;
    }

    this.requestingMagicLink = true;
    try {
      await this.loginService.requestMagicLink({
        email: raw,
        redirectTo: this.redirectTo,
      });
      this.enterMagicLinkWaitingState(raw);
      this.snackBar.open(MSG_LOGIN_LINK_SENT, 'dismiss', {
        duration: SNACK_DURATION_MS,
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : DEFAULT_MAGIC_LINK_REQUEST_FAILED;
      if (this.handleMagicLinkCooldownError(msg, raw)) {
        return;
      }
      if (this.magicLinkRequested) {
        this.snackBar.open(msg, 'dismiss', {
          duration: SNACK_DURATION_LONG_MS,
          verticalPosition: 'top',
          horizontalPosition: 'right',
        });
      } else if (this.isEmailNotFoundError(msg)) {
        this.magicLinkRequestError = null;
        this.emailNotFoundMessage = MSG_EMAIL_NOT_FOUND;
        this.userNameController.setErrors({
          ...(this.userNameController.errors ?? {}),
          [MAGIC_LINK_EMAIL_NOT_FOUND]: true,
        });
        this.userNameController.markAsDirty();
        this.userNameController.markAsTouched();
        this.userNameController.updateValueAndValidity({ emitEvent: true });
      } else {
        this.emailNotFoundMessage = null;
        this.magicLinkRequestError = msg;
        this.userNameController.markAsTouched();
      }
    } finally {
      this.requestingMagicLink = false;
    }
  }

  async verifyMagicLinkCode(): Promise<void> {
    this.clearMagicLinkVerifyError();
    const code = this.getJoinedMagicLinkCode();
    if (!MAGIC_LINK_CODE_PATTERN.test(code)) {
      this.magicLinkCodeDigitControls.forEach((c) => c.markAsTouched());
      return;
    }
    this.verifyingMagicLinkCode = true;
    try {
      await this.loginService.consumeMagicLink({
        email: this.magicLinkRecipientEmail,
        code,
      });
      this.magicLinkSessionEstablished.emit();
    } catch (e: unknown) {
      this.magicLinkCodeVerifyError =
        e instanceof Error ? e.message : DEFAULT_MAGIC_LINK_CODE_INVALID;
      this.magicLinkCodeDigitControls.forEach((c) => c.markAsTouched());
    } finally {
      this.verifyingMagicLinkCode = false;
    }
  }

  onMagicLinkDigitInput(index: number, event: Event): void {
    this.clearMagicLinkVerifyError();
    const input = event.target as HTMLInputElement;
    const digit = String(input.value ?? '')
      .replace(/\D/g, '')
      .slice(-1);
    this.magicLinkCodeDigitControls[index].setValue(digit, {
      emitEvent: false,
    });
    input.value = digit;
    if (digit && index < this.magicLinkCodeDigitControls.length - 1) {
      this.focusMagicLinkCodeBox(index + 1);
    }
  }

  onMagicLinkDigitKeydown(index: number, event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.magicLinkCodeComplete) {
      event.preventDefault();
      void this.verifyMagicLinkCode();
      return;
    }
    if (event.key !== 'Backspace') {
      return;
    }
    const cur = String(this.magicLinkCodeDigitControls[index].value ?? '');
    if (!cur && index > 0) {
      event.preventDefault();
      this.magicLinkCodeDigitControls[index - 1].setValue('', {
        emitEvent: false,
      });
      this.focusMagicLinkCodeBox(index - 1);
    }
  }

  onMagicLinkDigitPaste(index: number, event: ClipboardEvent): void {
    this.clearMagicLinkVerifyError();
    event.preventDefault();
    const text = event.clipboardData?.getData('text') ?? '';
    const digits = text
      .replace(/\D/g, '')
      .slice(0, this.magicLinkCodeDigitControls.length - index);
    for (let j = index; j < this.magicLinkCodeDigitControls.length; j++) {
      this.magicLinkCodeDigitControls[j].setValue('', { emitEvent: false });
    }
    for (let i = 0; i < digits.length; i++) {
      this.magicLinkCodeDigitControls[index + i].setValue(digits[i], {
        emitEvent: false,
      });
    }
    const lastFilled = index + Math.max(digits.length - 1, 0);
    const focusIdx = digits.length
      ? Math.min(lastFilled, this.magicLinkCodeDigitControls.length - 1)
      : index;
    this.focusMagicLinkCodeBox(focusIdx);
  }

  openCodeEntry(): void {
    this.showMagicLinkCodeField = true;
  }

  private enterMagicLinkWaitingState(
    email: string,
    waitSeconds?: number,
  ): void {
    this.screenDir = 'forward';
    this.magicLinkRecipientEmail = email;
    this.magicLinkRequested = true;
    this.magicLinkRequestError = null;
    this.emailNotFoundMessage = null;
    this.startMagicLinkCodeCountdown(waitSeconds);
  }

  private startMagicLinkCodeCountdown(waitSeconds?: number): void {
    this.clearMagicLinkCodeTimer();
    this.showMagicLinkCodeField = false;
    const total = waitSeconds ?? this.magicLinkCodeWaitSeconds;
    this.magicLinkCodeCountdownTotalSeconds = total;
    this.magicLinkCodeSecondsRemaining = total;
    this.resetMagicLinkCodeBoxes();
    this.magicLinkCodeTimerId = setInterval(() => {
      this.magicLinkCodeSecondsRemaining -= 1;
      if (this.magicLinkCodeSecondsRemaining <= 0) {
        this.clearMagicLinkCodeTimer();
        this.magicLinkCodeSecondsRemaining = 0;
        this.showMagicLinkCodeField = true;
        this.focusMagicLinkCodeBox(0);
      }
    }, 1000);
  }

  private clearMagicLinkCodeTimer(): void {
    if (this.magicLinkCodeTimerId != null) {
      clearInterval(this.magicLinkCodeTimerId);
      this.magicLinkCodeTimerId = null;
    }
  }

  private isEmailNotFoundError(message: string): boolean {
    const lower = message.toLowerCase();
    return (
      message.includes(MSG_EMAIL_NOT_FOUND) ||
      lower.includes('no account found for this email')
    );
  }

  private parseMagicLinkCooldownSeconds(message: string): number | null {
    const match = message.match(MAGIC_LINK_COOLDOWN_MESSAGE);
    if (!match) {
      return null;
    }
    const seconds = Number.parseInt(match[1], 10);
    return Number.isFinite(seconds) && seconds > 0 ? seconds : null;
  }

  /** Returns true when the error is a resend cooldown (handled in-page, no snackbar). */
  private handleMagicLinkCooldownError(
    message: string,
    email: string,
  ): boolean {
    const cooldownSeconds = this.parseMagicLinkCooldownSeconds(message);
    if (cooldownSeconds == null) {
      return false;
    }
    if (this.magicLinkRequested) {
      this.startMagicLinkCodeCountdown(cooldownSeconds);
    } else {
      this.enterMagicLinkWaitingState(email, cooldownSeconds);
    }
    return true;
  }

  clearMagicLinkFieldErrors(): void {
    this.magicLinkRequestError = null;
    this.emailNotFoundMessage = null;
    const errors = this.userNameController.errors as ValidationErrors | null;
    if (
      !errors?.[MAGIC_LINK_EMAIL_ERROR] &&
      !errors?.[MAGIC_LINK_EMAIL_NOT_FOUND]
    ) {
      return;
    }
    const {
      [MAGIC_LINK_EMAIL_ERROR]: _emailFormat,
      [MAGIC_LINK_EMAIL_NOT_FOUND]: _notFound,
      ...rest
    } = errors;
    this.userNameController.setErrors(Object.keys(rest).length ? rest : null);
  }

  private clearMagicLinkEmailError(): void {
    this.clearMagicLinkFieldErrors();
  }
}
